import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, X } from 'lucide-react';
import SelectField from './SelectField';

const FIELD_OPTIONS = [
  { value: 'from', label: 'Absender-Adresse' },
  { value: 'domain', label: 'Absender-Domain' },
  { value: 'subject', label: 'Betreff enthält' },
];

export function extractSpamHints(email) {
  const from = String(email?.from || '');
  const addrMatch = from.match(/<([^>]+)>/) || from.match(/([^\s<>]+@[^\s<>]+)/);
  const address = (addrMatch ? addrMatch[1] : '').trim().toLowerCase();
  const at = address.lastIndexOf('@');
  const domain = at >= 0 ? address.slice(at + 1) : '';
  const subject = String(email?.subject || '')
    .replace(/^(\s*(re|fw|fwd|aw)\s*:\s*)+/i, '')
    .trim();
  const subjectHint = subject
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 4)
    .join(' ')
    .slice(0, 48);
  return { address, domain, subject: subjectHint || subject.slice(0, 48) };
}

/** Haystack wie serverseitig (from / domain / subject). */
export function spamHaystackForField(email, field) {
  if (field === 'subject') return String(email?.subject || '');
  if (field === 'from') return String(email?.from || '');
  if (field === 'domain') {
    const from = String(email?.from || '');
    const match = from.match(/<([^>]+)>/) || from.match(/([^\s<>]+@[^\s<>]+)/);
    const addr = (match ? match[1] : from).trim().toLowerCase();
    const at = addr.lastIndexOf('@');
    return at >= 0 ? addr.slice(at + 1) : '';
  }
  return '';
}

export function emailMatchesSpamPattern(email, { field, pattern, matchMode = 'contains' } = {}) {
  const patternClean = String(pattern || '').trim();
  if (!patternClean || !email) return false;
  const haystack = spamHaystackForField(email, field);
  if (!haystack) return false;
  if (String(matchMode).toLowerCase() === 'regex') {
    try {
      return new RegExp(patternClean, 'i').test(haystack);
    } catch (_) {
      return false;
    }
  }
  const hay = haystack.toLowerCase();
  return patternClean
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .some((n) => hay.includes(n));
}

const CATEGORY_BUCKET_LABEL = {
  from: 'Eigene Absender-Filter',
  domain: 'Eigene Domain-Filter',
  subject: 'Eigene Betreff-Filter',
};

const categoryBucketSentinel = (field) => `__bucket__:${field}`;

/** Regeln der gewählten Kategorie (from / domain / subject), inkl. Standardregeln. */
export function findExtendableRules(spamRules, field) {
  return (spamRules?.rules || []).filter(
    (r) =>
      r &&
      r.field === field &&
      String(r.pattern || '').trim()
  );
}

/** Eigene Kategorie-Regel bevorzugen, sonst Sentinel für neue Kategorie-Regel. */
export function pickDefaultExtendRuleId(spamRules, field) {
  const list = findExtendableRules(spamRules, field);
  const bucketLabel = CATEGORY_BUCKET_LABEL[field];
  const bucket =
    list.find(
      (r) =>
        !String(r.id || '').startsWith('default-') &&
        String(r.label || '') === bucketLabel
    ) || list.find((r) => !String(r.id || '').startsWith('default-'));
  if (bucket) return String(bucket.id);
  return categoryBucketSentinel(field);
}

export function mergeSpamRuleIntoConfig(spamRules, { field, pattern, label, extendRuleId, createNew = false }) {
  const patternClean = String(pattern || '').trim();
  if (!patternClean || !['from', 'domain', 'subject'].includes(field)) {
    return null;
  }

  const needle = patternClean.toLowerCase();
  const rules = Array.isArray(spamRules?.rules) ? [...spamRules.rules] : [];
  const bucketLabel = CATEGORY_BUCKET_LABEL[field] || 'Eigene Filter';

  const appendPattern = (existingPattern) => {
    const parts = String(existingPattern || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const lower = new Set(parts.map((p) => p.toLowerCase()));
    if (!lower.has(needle)) parts.push(patternClean);
    return parts.join(', ');
  };

  const extendAt = (idx) => {
    const existing = rules[idx];
    rules[idx] = {
      ...existing,
      field,
      pattern: appendPattern(existing.pattern),
      enabled: true,
      matchMode: existing.matchMode === 'regex' ? 'regex' : 'contains',
    };
    return {
      enabled: true,
      version: spamRules?.version,
      rules,
      action: 'extended',
      rule: rules[idx],
    };
  };

  const already = rules.find((r) => {
    if (r.field !== field || r.enabled === false) return false;
    return String(r.pattern || '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean)
      .includes(needle);
  });
  if (already) {
    return {
      enabled: spamRules?.enabled !== false,
      version: spamRules?.version,
      rules,
      action: 'exists',
      rule: already,
    };
  }

  const targetRaw = extendRuleId != null ? String(extendRuleId) : '';
  const useBucket =
    !createNew && (!targetRaw || targetRaw === categoryBucketSentinel(field));

  if (!createNew && targetRaw && !useBucket) {
    const idx = rules.findIndex((r) => String(r.id) === targetRaw);
    if (idx >= 0) return extendAt(idx);
  }

  if (useBucket) {
    const exactIdx = rules.findIndex(
      (r) =>
        r.field === field &&
        !String(r.id || '').startsWith('default-') &&
        String(r.label || '') === bucketLabel
    );
    const customIdx = rules.findIndex(
      (r) => r.field === field && !String(r.id || '').startsWith('default-')
    );
    const idx = exactIdx >= 0 ? exactIdx : customIdx;
    if (idx >= 0) return extendAt(idx);

    const newRule = {
      id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      label: bucketLabel,
      field,
      pattern: patternClean,
      matchMode: 'contains',
      enabled: true,
    };
    rules.push(newRule);
    return {
      enabled: true,
      version: spamRules?.version,
      rules,
      action: 'created',
      rule: newRule,
    };
  }

  const newRule = {
    id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label:
      String(label || '').trim() ||
      (field === 'from'
        ? `Absender ${patternClean}`
        : field === 'domain'
          ? `Domain ${patternClean}`
          : `Betreff: ${patternClean}`),
    field,
    pattern: patternClean,
    matchMode: 'contains',
    enabled: true,
  };
  rules.push(newRule);
  return {
    enabled: true,
    version: spamRules?.version,
    rules,
    action: 'created',
    rule: newRule,
  };
}

const patternForField = (hints, field) => {
  if (field === 'from') return hints.address || '';
  if (field === 'domain') return hints.domain || '';
  return hints.subject || '';
};

const SpamMarkModal = ({ emails = [], spamRules, onCancel, onConfirm, busy }) => {
  const primary = emails[0] || null;
  const hints = useMemo(() => extractSpamHints(primary), [primary]);
  const count = emails.length;

  const [withRule, setWithRule] = useState(true);
  const [field, setField] = useState(
    hints.address ? 'from' : hints.domain ? 'domain' : 'subject'
  );
  const [pattern, setPattern] = useState(() => patternForField(hints, hints.address ? 'from' : hints.domain ? 'domain' : 'subject'));
  const [extendRuleId, setExtendRuleId] = useState(() =>
    pickDefaultExtendRuleId(spamRules, hints.address ? 'from' : hints.domain ? 'domain' : 'subject')
  );

  const extendable = useMemo(
    () => findExtendableRules(spamRules, field),
    [spamRules, field]
  );

  useEffect(() => {
    setPattern(patternForField(hints, field));
  }, [field, hints.address, hints.domain, hints.subject]);

  // Bei Kategoriewechsel: bestehende Regel der Kategorie vorauswählen
  useEffect(() => {
    setExtendRuleId(pickDefaultExtendRuleId(spamRules, field));
  }, [field, spamRules]);

  const canSaveRule = withRule && String(pattern || '').trim().length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    onConfirm({
      withRule: canSaveRule,
      field,
      pattern: String(pattern || '').trim(),
      extendRuleId: withRule && extendRuleId ? extendRuleId : null,
      createNew: withRule && !extendRuleId,
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-neutral-900/50 dark:bg-black/60" onClick={busy ? undefined : onCancel} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="spam-mark-title"
        className="relative w-full max-w-md rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 shadow-xl scale-in"
      >
        <form onSubmit={handleSubmit}>
          <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <h3 id="spam-mark-title" className="text-base font-semibold text-neutral-900 dark:text-white">
                  Als Spam markieren
                </h3>
              </div>
              <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                {count === 1
                  ? 'E-Mail in den Spam-Ordner verschieben.'
                  : `${count} E-Mails in den Spam-Ordner verschieben.`}
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-5 pb-4 space-y-3">
            {primary ? (
              <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-950/50 px-3 py-2.5 text-xs space-y-1">
                <p className="text-neutral-500 dark:text-neutral-400 truncate">
                  Von{' '}
                  <span className="font-medium text-neutral-800 dark:text-neutral-200">
                    {primary.from || 'Unbekannt'}
                  </span>
                </p>
                <p className="text-neutral-700 dark:text-neutral-300 truncate font-medium">
                  {primary.subject || '(kein Betreff)'}
                </p>
              </div>
            ) : null}

            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={withRule}
                onChange={(e) => setWithRule(e.target.checked)}
                disabled={busy}
                className="mt-0.5 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-800 dark:text-neutral-200">
                Filterregel speichern
                <span className="block text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Passende Mails in der Inbox werden sofort mitverschoben; künftig auch automatisch.
                </span>
              </span>
            </label>

            {withRule ? (
              <div className="space-y-3 pl-0 sm:pl-6">
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Kategorie
                  </label>
                  <SelectField
                    value={field}
                    onChange={(e) => setField(e.target.value)}
                    disabled={busy}
                    className="input-field !py-2 !text-sm"
                  >
                    {FIELD_OPTIONS.map((opt) => (
                      <option
                        key={opt.value}
                        value={opt.value}
                        disabled={
                          (opt.value === 'from' && !hints.address) ||
                          (opt.value === 'domain' && !hints.domain)
                        }
                      >
                        {opt.label}
                      </option>
                    ))}
                  </SelectField>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Muster
                  </label>
                  <input
                    type="text"
                    value={pattern}
                    onChange={(e) => setPattern(e.target.value)}
                    disabled={busy}
                    className="input-field !py-2 !text-sm"
                    placeholder={
                      field === 'from'
                        ? 'absender@beispiel.de'
                        : field === 'domain'
                          ? 'beispiel.de'
                          : 'Begriff im Betreff'
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
                    Zu bestehender Regel hinzufügen
                  </label>
                  <SelectField
                    value={extendRuleId}
                    onChange={(e) => setExtendRuleId(e.target.value)}
                    disabled={busy}
                    className="input-field !py-2 !text-sm"
                  >
                    <option value={`__bucket__:${field}`}>
                      {CATEGORY_BUCKET_LABEL[field] || 'Eigene Kategorie'}
                    </option>
                    {extendable.map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {r.label || r.pattern}
                        {String(r.id || '').startsWith('default-') ? ' (Standard)' : ''}
                      </option>
                    ))}
                    <option value="">Neue eigene Regel anlegen</option>
                  </SelectField>
                  <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                    Standard: Muster wird an die Kategorie-Regel angehängt (kommagetrennt).
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-neutral-100 dark:border-neutral-800">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="btn-secondary !py-2 !px-3 text-sm disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={busy || (withRule && !String(pattern || '').trim())}
              className="btn-primary !py-2 !px-3 text-sm inline-flex items-center disabled:opacity-50"
            >
              {busy ? (
                <>
                  <div className="spinner-sm mr-2" />
                  Wird markiert…
                </>
              ) : (
                <>
                  <ShieldAlert className="w-4 h-4 mr-1.5" />
                  Spam markieren
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default SpamMarkModal;
